// src/store/slices/karigar/jobSlice.js
// ============================================================================
// KARIGAR JOB SLICE - UPDATED VERSION
// ============================================================================
// Changes:
// 1. Added removeJobDeduction thunk import and handler
// 2. Added loading state for removeDeduction
// 3. Better payment state handling
// ============================================================================

import { createSlice } from "@reduxjs/toolkit";
import {
  // List & Search
  fetchJobs,
  fetchJobById,
  fetchJobStats,
  fetchJobTypes,

  // Job Creation
  createJob,
  createRepairJob,
  createAlterationJob,
  createInternalJob,
  receiveItemForWork,

  // Job Editing & Status
  updateJob,
  updateJobStatus,
  assignJob,
  reassignJob,
  cancelJob,
  cancelJobs,

  // Image Management
  fetchJobImages,
  uploadReferenceImages,
  deleteReferenceImage,
  uploadCompletedImages,
  deleteCompletedImage,
  uploadItemImages,
  uploadQCImages,
  bulkDeleteImages,
  uploadDesignFile,

  // Material Management - EXISTING
  fetchJobMaterials,
  issueMaterial,
  confirmMaterialReceived,
  recordMaterialReturn,

  // Material Management - NEW CRUD OPERATIONS
  updateIssuedMaterial,
  deleteIssuedMaterial,
  undoMaterialReceived,
  updateMaterialReturn,
  deleteMaterialReturn,

  // Quality Check Workflow
  submitForQualityCheck,
  performQualityCheck,
  completeRevision,
  markJobDelivered,

  // Job Payment
  fetchJobPayments,
  updateJobPricing,
  recordJobPayment,
  addJobDeduction,
  removeJobDeduction, // NEW
  fetchPricingPreview,

  // Progress
  addJobProgress,

  // Fetch by Relations
  fetchJobsByKarigar,
  fetchJobsByOrder,
  fetchJobTimeline,

  // Order-Karigar Integration
  fetchAvailableKarigarsForJob,
  assignKarigarToOrderItem,
  reassignKarigarForOrderItem,
  bulkAssignKarigars,
  getOrderKarigarStatus,
  getKarigarQueue,
  getOrderKarigarWorkStatus,
  assignKarigarViaOrder,
  checkOrderCanFulfill,
  sendJobMessage,
} from "../../thunks/karigar/jobThunks";

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState = {
  // List data
  jobs: [],
  currentJob: null,

  // Pagination
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  },

  // Filters
  filters: {
    status: "",
    karigarId: "",
    branchCode: "",
    priority: "",
    jobType: "",
    workType: "",
    orderId: "",
    search: "",
    dateFrom: "",
    dateTo: "",
    isOverdue: "",
  },

  // Loading states
  loading: {
    list: false,
    detail: false,
    create: false,
    update: false,
    status: false,
    assign: false,
    reassign: false,
    progress: false,
    complete: false,
    qualityCheck: false,
    approve: false,
    reject: false,
    rework: false,
    material: false,
    materialReturn: false,
    cancel: false,
    bulkCancel: false,
    stats: false,
    timeline: false,
    availableKarigars: false,
    orderStatus: false,
    karigarQueue: false,
    bulkAssign: false,
    jobTypes: false,
    jobMaterials: false,
    jobPayments: false,
    pricingPreview: false,
    pricing: false,
    payment: false,
    deduction: false,
    removeDeduction: false, // NEW
    submitQC: false,
    revision: false,
    deliver: false,
    canFulfill: false,
    message: false,
    // Image loading states
    fetchImages: false,
    uploadReferenceImages: false,
    deleteReferenceImage: false,
    uploadCompletedImages: false,
    deleteCompletedImage: false,
    uploadItemImages: false,
    uploadQCImages: false,
    bulkDeleteImages: false,
    uploadDesignFile: false,
    // Material CRUD loading states
    materialUpdate: false,
    materialDelete: false,
  },

  // Error and success messages
  error: null,
  successMessage: null,

  // Available karigars for assignment
  availableKarigars: {
    productKarigars: [],
    otherKarigars: [],
    allKarigars: [],
    recommendations: null,
    summary: null,
  },

  // Stats
  stats: null,

  // Timeline
  jobTimeline: [],

  // Order-Karigar status
  orderKarigarStatus: null,

  // Karigar queue
  karigarQueue: [],

  // Allowed transitions from API response
  allowedTransitions: [],
  canBeModified: false,

  // Material permissions from backend
  materialPermissions: {
    canIssueMaterial: false,
    canRecordReturn: false,
    canEditMaterials: false,
  },

  // Job Types (for dropdowns)
  jobTypes: null,

  // Current Job Materials
  currentJobMaterials: {
    issuedMaterials: [],
    returnedMaterials: [],
    materialSummary: null,
    wastage: null,
    returnRecord: null,
  },

  // Current Job Payments
  currentJobPayments: {
    job: null,
    pricing: null,
    karigarPayment: null,
    payments: {
      completed: [],
      pending: [],
    },
    materialValue: 0,
    wastageValue: 0,
  },

  pricingPreview: null,

  // Current Job Images
  currentJobImages: {
    referenceImages: [],
    itemImages: [],
    completedImages: [],
    qcImages: [],
    designFile: null,
  },

  // Order Karigar Work Status (from order service)
  orderKarigarWorkStatus: null,

  // Can Fulfill Check
  canFulfillCheck: null,
  orderDetails: null,
  productDetails: null,
};

// ============================================================================
// SLICE
// ============================================================================

const jobSlice = createSlice({
  name: "karigar/job",
  initialState,
  reducers: {
    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Clear success message
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },

    // Set filters
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    // Reset filters
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },

    // Clear current job
    clearCurrentJob: (state) => {
      state.currentJob = null;
      state.orderDetails = null; // Add this
      state.productDetails = null; // Add this
      state.allowedTransitions = [];
      state.canBeModified = false;
      state.materialPermissions = initialState.materialPermissions;
      state.currentJobMaterials = initialState.currentJobMaterials;
      state.currentJobPayments = initialState.currentJobPayments;
      state.currentJobImages = initialState.currentJobImages;
    },

    // Clear available karigars
    clearAvailableKarigars: (state) => {
      state.availableKarigars = initialState.availableKarigars;
    },

    // Clear timeline
    clearTimeline: (state) => {
      state.jobTimeline = [];
    },

    // Clear order status
    clearOrderStatus: (state) => {
      state.orderKarigarStatus = null;
    },

    // Clear karigar queue
    clearKarigarQueue: (state) => {
      state.karigarQueue = [];
    },

    // Clear job materials
    clearJobMaterials: (state) => {
      state.currentJobMaterials = initialState.currentJobMaterials;
    },

    // Clear job payments
    clearJobPayments: (state) => {
      state.currentJobPayments = initialState.currentJobPayments;
    },

    // Clear job images
    clearJobImages: (state) => {
      state.currentJobImages = initialState.currentJobImages;
    },

    clearPricingPreview: (state) => {
      state.pricingPreview = null;
    },

    // Clear can fulfill check
    clearCanFulfillCheck: (state) => {
      state.canFulfillCheck = null;
    },

    // Clear order karigar work status
    clearOrderKarigarWorkStatus: (state) => {
      state.orderKarigarWorkStatus = null;
    },

    // Set material permissions manually
    setMaterialPermissions: (state, action) => {
      state.materialPermissions = {
        ...state.materialPermissions,
        ...action.payload,
      };
    },
  },

  extraReducers: (builder) => {
    // ========================================================================
    // FETCH JOBS (List)
    // ========================================================================
    builder
      .addCase(fetchJobs.pending, (state) => {
        state.loading.list = true;
        state.error = null;
      })
      .addCase(fetchJobs.fulfilled, (state, action) => {
        state.loading.list = false;
        const data = action.payload?.data || action.payload;
        state.jobs = data?.jobs || data || [];
        if (data?.pagination) {
          state.pagination = data.pagination;
        }
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        state.loading.list = false;
        state.error = action.payload?.message || "Failed to fetch jobs";
      });

    // ========================================================================
    // FETCH JOB BY ID
    // ========================================================================
    builder
      .addCase(fetchJobById.pending, (state) => {
        state.loading.detail = true;
        state.error = null;
      })
      .addCase(fetchJobById.fulfilled, (state, action) => {
        state.loading.detail = false;
        const data = action.payload?.data || action.payload;

        // Store the job
        state.currentJob = data?.job || data;

        // Extract orderDetails and productDetails from API response
        state.orderDetails = data?.orderDetails || null;
        state.productDetails = data?.productDetails || null;

        // Extract allowedTransitions and canBeModified
        state.allowedTransitions = data?.allowedTransitions || [];
        state.canBeModified = data?.canBeModified ?? false;

        // Extract material permissions
        state.materialPermissions = {
          canIssueMaterial: data?.canIssueMaterial ?? false,
          canRecordReturn: data?.canRecordReturn ?? false,
          canEditMaterials: data?.canEditMaterials ?? false,
        };
      })
      .addCase(fetchJobById.rejected, (state, action) => {
        state.loading.detail = false;
        state.error = action.payload?.message || "Failed to fetch job";
      });

    // ========================================================================
    // FETCH JOB STATS
    // ========================================================================
    builder
      .addCase(fetchJobStats.pending, (state) => {
        state.loading.stats = true;
        state.error = null;
      })
      .addCase(fetchJobStats.fulfilled, (state, action) => {
        state.loading.stats = false;
        state.stats = action.payload?.data || action.payload;
      })
      .addCase(fetchJobStats.rejected, (state, action) => {
        state.loading.stats = false;
        state.error = action.payload?.message || "Failed to fetch stats";
      });

    // ========================================================================
    // FETCH JOB TYPES
    // ========================================================================
    builder
      .addCase(fetchJobTypes.pending, (state) => {
        state.loading.jobTypes = true;
      })
      .addCase(fetchJobTypes.fulfilled, (state, action) => {
        state.loading.jobTypes = false;
        state.jobTypes = action.payload?.data || action.payload;
      })
      .addCase(fetchJobTypes.rejected, (state, action) => {
        state.loading.jobTypes = false;
        state.error = action.payload?.message || "Failed to fetch job types";
      });

    // ========================================================================
    // CREATE JOB
    // ========================================================================
    builder
      .addCase(createJob.pending, (state) => {
        state.loading.create = true;
        state.error = null;
      })
      .addCase(createJob.fulfilled, (state, action) => {
        state.loading.create = false;
        state.successMessage = "Job created successfully";
        const newJob = action.payload?.data?.job || action.payload;
        if (newJob && !Array.isArray(state.jobs)) {
          state.jobs = [newJob, ...state.jobs];
        }
      })
      .addCase(createJob.rejected, (state, action) => {
        state.loading.create = false;
        state.error = action.payload?.message || "Failed to create job";
      });

    // CREATE REPAIR JOB
    builder
      .addCase(createRepairJob.pending, (state) => {
        state.loading.create = true;
        state.error = null;
      })
      .addCase(createRepairJob.fulfilled, (state, action) => {
        state.loading.create = false;
        state.successMessage = "Repair job created successfully";
      })
      .addCase(createRepairJob.rejected, (state, action) => {
        state.loading.create = false;
        state.error = action.payload?.message || "Failed to create repair job";
      });

    // CREATE ALTERATION JOB
    builder
      .addCase(createAlterationJob.pending, (state) => {
        state.loading.create = true;
        state.error = null;
      })
      .addCase(createAlterationJob.fulfilled, (state, action) => {
        state.loading.create = false;
        state.successMessage = "Alteration job created successfully";
      })
      .addCase(createAlterationJob.rejected, (state, action) => {
        state.loading.create = false;
        state.error =
          action.payload?.message || "Failed to create alteration job";
      });

    // CREATE INTERNAL JOB
    builder
      .addCase(createInternalJob.pending, (state) => {
        state.loading.create = true;
        state.error = null;
      })
      .addCase(createInternalJob.fulfilled, (state, action) => {
        state.loading.create = false;
        state.successMessage = "Internal job created successfully";
      })
      .addCase(createInternalJob.rejected, (state, action) => {
        state.loading.create = false;
        state.error =
          action.payload?.message || "Failed to create internal job";
      });

    // RECEIVE ITEM FOR WORK
    builder
      .addCase(receiveItemForWork.pending, (state) => {
        state.loading.create = true;
        state.error = null;
      })
      .addCase(receiveItemForWork.fulfilled, (state, action) => {
        state.loading.create = false;
        state.successMessage = "Item received for work";
      })
      .addCase(receiveItemForWork.rejected, (state, action) => {
        state.loading.create = false;
        state.error = action.payload?.message || "Failed to receive item";
      });

    // ========================================================================
    // UPDATE JOB
    // ========================================================================
    builder
      .addCase(updateJob.pending, (state) => {
        state.loading.update = true;
        state.error = null;
      })
      .addCase(updateJob.fulfilled, (state, action) => {
        state.loading.update = false;
        state.successMessage = "Job updated successfully";
        const updatedJob = action.payload?.data?.job || action.payload;
        if (updatedJob) {
          state.currentJob = updatedJob;
          const index = state.jobs.findIndex((j) => j._id === updatedJob._id);
          if (index !== -1) {
            state.jobs[index] = updatedJob;
          }
        }
      })
      .addCase(updateJob.rejected, (state, action) => {
        state.loading.update = false;
        state.error = action.payload?.message || "Failed to update job";
      });

    // ========================================================================
    // UPDATE JOB STATUS
    // ========================================================================
    builder
      .addCase(updateJobStatus.pending, (state) => {
        state.loading.status = true;
        state.error = null;
      })
      .addCase(updateJobStatus.fulfilled, (state, action) => {
        state.loading.status = false;
        state.successMessage = "Job status updated";
        const { jobId, newStatus } = action.payload;
        if (state.currentJob?._id === jobId) {
          state.currentJob.status = newStatus;
        }
        const index = state.jobs.findIndex((j) => j._id === jobId);
        if (index !== -1) {
          state.jobs[index].status = newStatus;
        }
      })
      .addCase(updateJobStatus.rejected, (state, action) => {
        state.loading.status = false;
        state.error = action.payload?.message || "Failed to update status";
      });

    // ========================================================================
    // ASSIGN JOB
    // ========================================================================
    builder
      .addCase(assignJob.pending, (state) => {
        state.loading.assign = true;
        state.error = null;
      })
      .addCase(assignJob.fulfilled, (state, action) => {
        state.loading.assign = false;
        state.successMessage = "Karigar assigned successfully";
        const updatedJob = action.payload?.data?.job || action.payload;
        if (updatedJob) {
          state.currentJob = updatedJob;
        }
      })
      .addCase(assignJob.rejected, (state, action) => {
        state.loading.assign = false;
        state.error = action.payload?.message || "Failed to assign karigar";
      });

    // ========================================================================
    // REASSIGN JOB
    // ========================================================================
    builder
      .addCase(reassignJob.pending, (state) => {
        state.loading.reassign = true;
        state.error = null;
      })
      .addCase(reassignJob.fulfilled, (state, action) => {
        state.loading.reassign = false;
        state.successMessage = "Karigar reassigned successfully";
        const updatedJob = action.payload?.data?.job || action.payload;
        if (updatedJob) {
          state.currentJob = updatedJob;
        }
      })
      .addCase(reassignJob.rejected, (state, action) => {
        state.loading.reassign = false;
        state.error = action.payload?.message || "Failed to reassign karigar";
      });

    // ========================================================================
    // CANCEL JOB
    // ========================================================================
    builder
      .addCase(cancelJob.pending, (state) => {
        state.loading.cancel = true;
        state.error = null;
      })
      .addCase(cancelJob.fulfilled, (state, action) => {
        state.loading.cancel = false;
        state.successMessage = "Job cancelled";
        const { jobId } = action.payload;
        if (state.currentJob?._id === jobId) {
          state.currentJob.status = "CANCELLED";
        }
        const index = state.jobs.findIndex((j) => j._id === jobId);
        if (index !== -1) {
          state.jobs[index].status = "CANCELLED";
        }
      })
      .addCase(cancelJob.rejected, (state, action) => {
        state.loading.cancel = false;
        state.error = action.payload?.message || "Failed to cancel job";
      });

    // CANCEL MULTIPLE JOBS
    builder
      .addCase(cancelJobs.pending, (state) => {
        state.loading.bulkCancel = true;
        state.error = null;
      })
      .addCase(cancelJobs.fulfilled, (state, action) => {
        state.loading.bulkCancel = false;
        state.successMessage = `${action.payload.count} jobs cancelled`;
      })
      .addCase(cancelJobs.rejected, (state, action) => {
        state.loading.bulkCancel = false;
        state.error = action.payload?.message || "Failed to cancel jobs";
      });

    // ========================================================================
    // IMAGE MANAGEMENT
    // ========================================================================

    // Fetch Job Images
    builder
      .addCase(fetchJobImages.pending, (state) => {
        state.loading.fetchImages = true;
        state.error = null;
      })
      .addCase(fetchJobImages.fulfilled, (state, action) => {
        state.loading.fetchImages = false;
        const data = action.payload?.data || action.payload;
        state.currentJobImages = {
          referenceImages: data?.referenceImages || [],
          itemImages: data?.itemImages || [],
          completedImages: data?.completedImages || [],
          qcImages: data?.qcImages || [],
          designFile: data?.designFile || null,
        };
      })
      .addCase(fetchJobImages.rejected, (state, action) => {
        state.loading.fetchImages = false;
        state.error = action.payload?.message || "Failed to fetch images";
      });

    // Upload Reference Images
    builder
      .addCase(uploadReferenceImages.pending, (state) => {
        state.loading.uploadReferenceImages = true;
        state.error = null;
      })
      .addCase(uploadReferenceImages.fulfilled, (state, action) => {
        state.loading.uploadReferenceImages = false;
        state.successMessage = "Reference images uploaded";
        const data = action.payload?.data || action.payload;
        if (data?.images) {
          state.currentJobImages.referenceImages = [
            ...state.currentJobImages.referenceImages,
            ...data.images,
          ];
        }
        if (data?.referenceImages) {
          state.currentJobImages.referenceImages = data.referenceImages;
        }
      })
      .addCase(uploadReferenceImages.rejected, (state, action) => {
        state.loading.uploadReferenceImages = false;
        state.error =
          action.payload?.message || "Failed to upload reference images";
      });

    // Delete Reference Image
    builder
      .addCase(deleteReferenceImage.pending, (state) => {
        state.loading.deleteReferenceImage = true;
        state.error = null;
      })
      .addCase(deleteReferenceImage.fulfilled, (state, action) => {
        state.loading.deleteReferenceImage = false;
        state.successMessage = "Image deleted";
        const { imageUrl } = action.payload;
        state.currentJobImages.referenceImages =
          state.currentJobImages.referenceImages.filter(
            (img) => (img.url || img) !== imageUrl
          );
      })
      .addCase(deleteReferenceImage.rejected, (state, action) => {
        state.loading.deleteReferenceImage = false;
        state.error = action.payload?.message || "Failed to delete image";
      });

    // Upload Completed Images
    builder
      .addCase(uploadCompletedImages.pending, (state) => {
        state.loading.uploadCompletedImages = true;
        state.error = null;
      })
      .addCase(uploadCompletedImages.fulfilled, (state, action) => {
        state.loading.uploadCompletedImages = false;
        state.successMessage = "Completed images uploaded";
        const data = action.payload?.data || action.payload;
        if (data?.images) {
          state.currentJobImages.completedImages = [
            ...state.currentJobImages.completedImages,
            ...data.images,
          ];
        }
        if (data?.completedImages) {
          state.currentJobImages.completedImages = data.completedImages;
        }
      })
      .addCase(uploadCompletedImages.rejected, (state, action) => {
        state.loading.uploadCompletedImages = false;
        state.error =
          action.payload?.message || "Failed to upload completed images";
      });

    // Delete Completed Image
    builder
      .addCase(deleteCompletedImage.pending, (state) => {
        state.loading.deleteCompletedImage = true;
        state.error = null;
      })
      .addCase(deleteCompletedImage.fulfilled, (state, action) => {
        state.loading.deleteCompletedImage = false;
        state.successMessage = "Image deleted";
        const { imageUrl } = action.payload;
        state.currentJobImages.completedImages =
          state.currentJobImages.completedImages.filter(
            (img) => (img.url || img) !== imageUrl
          );
      })
      .addCase(deleteCompletedImage.rejected, (state, action) => {
        state.loading.deleteCompletedImage = false;
        state.error = action.payload?.message || "Failed to delete image";
      });

    // Upload Item Images
    builder
      .addCase(uploadItemImages.pending, (state) => {
        state.loading.uploadItemImages = true;
        state.error = null;
      })
      .addCase(uploadItemImages.fulfilled, (state, action) => {
        state.loading.uploadItemImages = false;
        state.successMessage = "Item images uploaded";
        const data = action.payload?.data || action.payload;
        if (data?.images) {
          state.currentJobImages.itemImages = [
            ...state.currentJobImages.itemImages,
            ...data.images,
          ];
        }
        if (data?.itemImages) {
          state.currentJobImages.itemImages = data.itemImages;
        }
      })
      .addCase(uploadItemImages.rejected, (state, action) => {
        state.loading.uploadItemImages = false;
        state.error = action.payload?.message || "Failed to upload item images";
      });

    // Upload QC Images
    builder
      .addCase(uploadQCImages.pending, (state) => {
        state.loading.uploadQCImages = true;
        state.error = null;
      })
      .addCase(uploadQCImages.fulfilled, (state, action) => {
        state.loading.uploadQCImages = false;
        state.successMessage = "QC images uploaded";
        const data = action.payload?.data || action.payload;
        if (data?.images) {
          state.currentJobImages.qcImages = [
            ...state.currentJobImages.qcImages,
            ...data.images,
          ];
        }
        if (data?.qcImages) {
          state.currentJobImages.qcImages = data.qcImages;
        }
      })
      .addCase(uploadQCImages.rejected, (state, action) => {
        state.loading.uploadQCImages = false;
        state.error = action.payload?.message || "Failed to upload QC images";
      });

    // Bulk Delete Images
    builder
      .addCase(bulkDeleteImages.pending, (state) => {
        state.loading.bulkDeleteImages = true;
        state.error = null;
      })
      .addCase(bulkDeleteImages.fulfilled, (state, action) => {
        state.loading.bulkDeleteImages = false;
        state.successMessage = "Images deleted";
        const { imageUrls, imageType } = action.payload;
        if (imageType && state.currentJobImages[imageType]) {
          state.currentJobImages[imageType] = state.currentJobImages[
            imageType
          ].filter((img) => !imageUrls.includes(img.url || img));
        }
      })
      .addCase(bulkDeleteImages.rejected, (state, action) => {
        state.loading.bulkDeleteImages = false;
        state.error = action.payload?.message || "Failed to delete images";
      });

    // Upload Design File
    builder
      .addCase(uploadDesignFile.pending, (state) => {
        state.loading.uploadDesignFile = true;
        state.error = null;
      })
      .addCase(uploadDesignFile.fulfilled, (state, action) => {
        state.loading.uploadDesignFile = false;
        state.successMessage = "Design file uploaded";
        const data = action.payload?.data || action.payload;
        if (data?.designFile) {
          state.currentJobImages.designFile = data.designFile;
        }
      })
      .addCase(uploadDesignFile.rejected, (state, action) => {
        state.loading.uploadDesignFile = false;
        state.error = action.payload?.message || "Failed to upload design file";
      });

    // ========================================================================
    // MATERIAL MANAGEMENT - EXISTING
    // ========================================================================

    // Fetch Job Materials
    builder
      .addCase(fetchJobMaterials.pending, (state) => {
        state.loading.jobMaterials = true;
      })
      .addCase(fetchJobMaterials.fulfilled, (state, action) => {
        state.loading.jobMaterials = false;
        const data = action.payload?.data || action.payload;
        state.currentJobMaterials = {
          issuedMaterials: data?.issuedMaterials || [],
          returnedMaterials: data?.returnedMaterials || [],
          materialSummary: data?.summary || data?.materialSummary || null,
          wastage: data?.wastage || null,
          returnRecord: data?.returnedMaterials || null,
        };
        // Update permissions if provided
        if (data?.permissions) {
          state.materialPermissions = data.permissions;
        }
      })
      .addCase(fetchJobMaterials.rejected, (state, action) => {
        state.loading.jobMaterials = false;
        state.error = action.payload?.message || "Failed to fetch materials";
      });

    // Issue Material
    builder
      .addCase(issueMaterial.pending, (state) => {
        state.loading.material = true;
        state.error = null;
      })
      .addCase(issueMaterial.fulfilled, (state, action) => {
        state.loading.material = false;
        state.successMessage = "Material issued successfully";
        const data = action.payload?.data || action.payload;
        if (data?.job?.materialsIssued) {
          if (state.currentJob) {
            state.currentJob.materialsIssued = data.job.materialsIssued;
          }
        }
        if (data?.material || data?.materialIssued) {
          state.currentJobMaterials.issuedMaterials.push(
            data.material || data.materialIssued
          );
        }
        if (data?.allMaterials) {
          state.currentJobMaterials.issuedMaterials = data.allMaterials;
        }
        if (data?.materialSummary) {
          state.currentJobMaterials.materialSummary = data.materialSummary;
        }
      })
      .addCase(issueMaterial.rejected, (state, action) => {
        state.loading.material = false;
        state.error = action.payload?.message || "Failed to issue material";
      });

    // Confirm Material Received
    builder
      .addCase(confirmMaterialReceived.pending, (state) => {
        state.loading.material = true;
        state.error = null;
      })
      .addCase(confirmMaterialReceived.fulfilled, (state, action) => {
        state.loading.material = false;
        state.successMessage = "Material receipt confirmed";
        const data = action.payload?.data || action.payload;
        const materialId = data?.materialId || action.payload?.materialId;

        // Update in currentJob
        if (materialId && state.currentJob?.materialsIssued) {
          const material = state.currentJob.materialsIssued.find(
            (m) => m._id === materialId
          );
          if (material) {
            material.receivedByKarigar = true;
            material.receivedAt = new Date().toISOString();
          }
        }

        // Update in currentJobMaterials
        if (materialId) {
          const idx = state.currentJobMaterials.issuedMaterials.findIndex(
            (m) => m._id === materialId
          );
          if (idx !== -1) {
            state.currentJobMaterials.issuedMaterials[
              idx
            ].receivedByKarigar = true;
            state.currentJobMaterials.issuedMaterials[idx].receivedAt =
              new Date().toISOString();
          }
        }

        // Or update from response
        if (data?.material) {
          const idx = state.currentJobMaterials.issuedMaterials.findIndex(
            (m) => m._id === data.material._id
          );
          if (idx !== -1) {
            state.currentJobMaterials.issuedMaterials[idx] = data.material;
          }
        }
        if (data?.allMaterials) {
          state.currentJobMaterials.issuedMaterials = data.allMaterials;
        }
      })
      .addCase(confirmMaterialReceived.rejected, (state, action) => {
        state.loading.material = false;
        state.error = action.payload?.message || "Failed to confirm receipt";
      });

    // Record Material Return
    builder
      .addCase(recordMaterialReturn.pending, (state) => {
        state.loading.materialReturn = true;
        state.error = null;
      })
      .addCase(recordMaterialReturn.fulfilled, (state, action) => {
        state.loading.materialReturn = false;
        state.successMessage = "Material return recorded";
        const data = action.payload?.data || action.payload;

        if (data?.materialSummary) {
          state.currentJobMaterials.materialSummary = data.materialSummary;
        }

        if (data?.materialsReturned) {
          state.currentJobMaterials.wastage = {
            wastageWeight: data.materialsReturned.wastageWeight,
            wastagePercentage: data.materialsReturned.wastagePercentage,
          };
          state.currentJobMaterials.returnRecord = data.materialsReturned;
        }

        if (data?.wastage) {
          state.currentJobMaterials.wastage = data.wastage;
        }

        if (state.currentJob) {
          state.currentJob.materialsReturned = data.materialsReturned;
          if (!state.currentJob.completionDetails) {
            state.currentJob.completionDetails = {};
          }
          if (data.materialsReturned?.finishedWeight) {
            state.currentJob.completionDetails.finalWeight =
              data.materialsReturned.finishedWeight;
          }
          if (data.materialsReturned?.finishedPurity) {
            state.currentJob.completionDetails.finalPurity =
              data.materialsReturned.finishedPurity;
          }
        }
      })
      .addCase(recordMaterialReturn.rejected, (state, action) => {
        state.loading.materialReturn = false;
        state.error = action.payload?.message || "Failed to record return";
      });

    builder
      .addCase(fetchPricingPreview.pending, (state) => {
        state.loading.pricingPreview = true;
        state.error = null;
      })
      .addCase(fetchPricingPreview.fulfilled, (state, action) => {
        state.loading.pricingPreview = false;
        state.pricingPreview = action.payload?.data || action.payload;
      })
      .addCase(fetchPricingPreview.rejected, (state, action) => {
        state.loading.pricingPreview = false;
        state.error =
          action.payload?.message || "Failed to fetch pricing preview";
      });

    // ========================================================================
    // MATERIAL MANAGEMENT - NEW CRUD OPERATIONS
    // ========================================================================

    // Update Issued Material
    builder
      .addCase(updateIssuedMaterial.pending, (state) => {
        state.loading.materialUpdate = true;
        state.error = null;
      })
      .addCase(updateIssuedMaterial.fulfilled, (state, action) => {
        state.loading.materialUpdate = false;
        state.successMessage =
          action.payload?.message || "Material updated successfully";
        const data = action.payload?.data || action.payload;

        // Update the specific material in list
        if (data?.material) {
          const index = state.currentJobMaterials.issuedMaterials.findIndex(
            (m) => m._id === data.material._id
          );
          if (index !== -1) {
            state.currentJobMaterials.issuedMaterials[index] = data.material;
          }
        }

        // Update summary
        if (data?.materialSummary) {
          state.currentJobMaterials.materialSummary = data.materialSummary;
        }
      })
      .addCase(updateIssuedMaterial.rejected, (state, action) => {
        state.loading.materialUpdate = false;
        state.error = action.payload?.message || "Failed to update material";
      });

    // Delete Issued Material
    builder
      .addCase(deleteIssuedMaterial.pending, (state) => {
        state.loading.materialDelete = true;
        state.error = null;
      })
      .addCase(deleteIssuedMaterial.fulfilled, (state, action) => {
        state.loading.materialDelete = false;
        state.successMessage =
          action.payload?.message || "Material removed successfully";
        const { materialId } = action.payload;
        const data = action.payload?.data || action.payload;

        // Remove from list
        if (materialId) {
          state.currentJobMaterials.issuedMaterials =
            state.currentJobMaterials.issuedMaterials.filter(
              (m) => m._id !== materialId
            );
        } else if (data?.remainingMaterials) {
          state.currentJobMaterials.issuedMaterials = data.remainingMaterials;
        }

        // Update summary
        if (data?.materialSummary) {
          state.currentJobMaterials.materialSummary = data.materialSummary;
        }
      })
      .addCase(deleteIssuedMaterial.rejected, (state, action) => {
        state.loading.materialDelete = false;
        state.error = action.payload?.message || "Failed to delete material";
      });

    // Undo Material Received
    builder
      .addCase(undoMaterialReceived.pending, (state) => {
        state.loading.material = true;
        state.error = null;
      })
      .addCase(undoMaterialReceived.fulfilled, (state, action) => {
        state.loading.material = false;
        state.successMessage =
          action.payload?.message || "Material receipt undone";
        const { materialId } = action.payload;
        const data = action.payload?.data || action.payload;

        if (materialId && data?.material) {
          const index = state.currentJobMaterials.issuedMaterials.findIndex(
            (m) => m._id === materialId
          );
          if (index !== -1) {
            state.currentJobMaterials.issuedMaterials[index] = data.material;
          }
        } else if (data?.allMaterials) {
          state.currentJobMaterials.issuedMaterials = data.allMaterials;
        }
      })
      .addCase(undoMaterialReceived.rejected, (state, action) => {
        state.loading.material = false;
        state.error =
          action.payload?.message || "Failed to undo material receipt";
      });

    // Update Material Return
    builder
      .addCase(updateMaterialReturn.pending, (state) => {
        state.loading.materialReturn = true;
        state.error = null;
      })
      .addCase(updateMaterialReturn.fulfilled, (state, action) => {
        state.loading.materialReturn = false;
        state.successMessage =
          action.payload?.message || "Material return adjusted";
        const data = action.payload?.data || action.payload;

        if (data?.materialsReturned) {
          state.currentJobMaterials.returnRecord = data.materialsReturned;
          state.currentJobMaterials.wastage = {
            wastageWeight: data.materialsReturned.wastageWeight,
            wastagePercentage: data.materialsReturned.wastagePercentage,
          };
          if (state.currentJob) {
            state.currentJob.materialsReturned = data.materialsReturned;
          }
        }

        if (data?.wastage) {
          state.currentJobMaterials.wastage = data.wastage;
        }

        if (data?.materialSummary) {
          state.currentJobMaterials.materialSummary = data.materialSummary;
        }
      })
      .addCase(updateMaterialReturn.rejected, (state, action) => {
        state.loading.materialReturn = false;
        state.error =
          action.payload?.message || "Failed to adjust material return";
      });

    // Delete Material Return
    builder
      .addCase(deleteMaterialReturn.pending, (state) => {
        state.loading.materialReturn = true;
        state.error = null;
      })
      .addCase(deleteMaterialReturn.fulfilled, (state, action) => {
        state.loading.materialReturn = false;
        state.successMessage =
          action.payload?.message || "Material return record deleted";
        const data = action.payload?.data || action.payload;

        // Reset return data
        state.currentJobMaterials.returnRecord = null;
        state.currentJobMaterials.wastage = null;

        if (data?.materialSummary) {
          state.currentJobMaterials.materialSummary = data.materialSummary;
        }

        if (state.currentJob) {
          state.currentJob.materialsReturned = null;
        }
      })
      .addCase(deleteMaterialReturn.rejected, (state, action) => {
        state.loading.materialReturn = false;
        state.error =
          action.payload?.message || "Failed to delete material return";
      });

    // ========================================================================
    // QUALITY CHECK WORKFLOW
    // ========================================================================

    // Submit for QC
    builder
      .addCase(submitForQualityCheck.pending, (state) => {
        state.loading.submitQC = true;
        state.error = null;
      })
      .addCase(submitForQualityCheck.fulfilled, (state, action) => {
        state.loading.submitQC = false;
        state.successMessage = "Submitted for quality check";
        const data = action.payload?.data || action.payload;
        if (state.currentJob) {
          state.currentJob.status = "QUALITY_CHECK";
          if (data?.job?.completionDetails) {
            state.currentJob.completionDetails = data.job.completionDetails;
          }
        }
      })
      .addCase(submitForQualityCheck.rejected, (state, action) => {
        state.loading.submitQC = false;
        state.error = action.payload?.message || "Failed to submit for QC";
      });

    // Perform Quality Check
    builder
      .addCase(performQualityCheck.pending, (state) => {
        state.loading.qualityCheck = true;
        state.error = null;
      })
      .addCase(performQualityCheck.fulfilled, (state, action) => {
        state.loading.qualityCheck = false;
        const data = action.payload?.data || action.payload;
        const passed = data?.passed ?? action.meta?.arg?.passed;
        state.successMessage = passed
          ? "Quality check passed - Job completed"
          : "Revision required";
        if (state.currentJob) {
          state.currentJob.status = passed ? "COMPLETED" : "REVISION_REQUIRED";
          if (data?.job?.completionDetails) {
            state.currentJob.completionDetails = data.job.completionDetails;
          }
        }
      })
      .addCase(performQualityCheck.rejected, (state, action) => {
        state.loading.qualityCheck = false;
        state.error =
          action.payload?.message || "Failed to perform quality check";
      });

    // Complete Revision
    builder
      .addCase(completeRevision.pending, (state) => {
        state.loading.revision = true;
        state.error = null;
      })
      .addCase(completeRevision.fulfilled, (state, action) => {
        state.loading.revision = false;
        state.successMessage = "Revision completed - Resubmitted for QC";
        if (state.currentJob) {
          state.currentJob.status = "QUALITY_CHECK";
        }
      })
      .addCase(completeRevision.rejected, (state, action) => {
        state.loading.revision = false;
        state.error = action.payload?.message || "Failed to complete revision";
      });

    // Mark Delivered
    builder
      .addCase(markJobDelivered.pending, (state) => {
        state.loading.deliver = true;
        state.error = null;
      })
      .addCase(markJobDelivered.fulfilled, (state, action) => {
        state.loading.deliver = false;
        state.successMessage = "Job marked as delivered";
        if (state.currentJob) {
          state.currentJob.status = "DELIVERED";
        }
      })
      .addCase(markJobDelivered.rejected, (state, action) => {
        state.loading.deliver = false;
        state.error =
          action.payload?.message || "Failed to mark job as delivered";
      });

    // ========================================================================
    // JOB PAYMENTS
    // ========================================================================

    // Fetch Job Payments
    builder
      .addCase(fetchJobPayments.pending, (state) => {
        state.loading.jobPayments = true;
      })
      .addCase(fetchJobPayments.fulfilled, (state, action) => {
        state.loading.jobPayments = false;
        const data = action.payload?.data || action.payload;
        state.currentJobPayments = {
          job: data?.job || null,
          pricing: data?.pricing || null,
          karigarPayment: data?.karigarPayment || null,
          payments: data?.payments || { completed: [], pending: [] },
          materialValue: data?.materialValue || 0,
          wastageValue: data?.wastageValue || 0,
        };
      })
      .addCase(fetchJobPayments.rejected, (state, action) => {
        state.loading.jobPayments = false;
        state.error = action.payload?.message || "Failed to fetch payments";
      });

    // Update Job Pricing
    builder
      .addCase(updateJobPricing.pending, (state) => {
        state.loading.pricing = true;
        state.error = null;
      })
      .addCase(updateJobPricing.fulfilled, (state, action) => {
        state.loading.pricing = false;
        state.successMessage = "Pricing updated";
        const data = action.payload?.data || action.payload;
        if (data?.pricing) {
          state.currentJobPayments.pricing = data.pricing;
          if (state.currentJob) {
            state.currentJob.pricing = data.pricing;
          }
        }
        // Update karigar payable if provided
        if (data?.karigarPayable !== undefined) {
          state.currentJobPayments.karigarPayment = {
            ...state.currentJobPayments.karigarPayment,
            totalPayable: data.karigarPayable,
          };
        }
      })
      .addCase(updateJobPricing.rejected, (state, action) => {
        state.loading.pricing = false;
        state.error = action.payload?.message || "Failed to update pricing";
      });

    // Record Job Payment
    builder
      .addCase(recordJobPayment.pending, (state) => {
        state.loading.payment = true;
        state.error = null;
      })
      .addCase(recordJobPayment.fulfilled, (state, action) => {
        state.loading.payment = false;
        const data = action.payload?.data || action.payload;
        const status = data?.status || "pending";
        state.successMessage =
          status === "completed"
            ? "Payment processed"
            : "Payment created (pending approval)";

        // Add to appropriate list based on status
        if (data?.paymentId || data?.payment) {
          const payment = data.payment || {
            _id: data.paymentId,
            paymentReference: data.paymentReference,
            amount: data.netAmount,
            status: status,
          };

          if (status === "completed") {
            state.currentJobPayments.payments.completed.push(payment);
          } else {
            state.currentJobPayments.payments.pending.push(payment);
          }
        }

        // Update totals from response
        if (data?.totalPaid !== undefined) {
          if (state.currentJobPayments.karigarPayment) {
            state.currentJobPayments.karigarPayment.totalPaid = data.totalPaid;
          }
        }
        if (data?.pricing) {
          state.currentJobPayments.pricing = data.pricing;
          if (state.currentJob) {
            state.currentJob.pricing = data.pricing;
          }
        }
      })
      .addCase(recordJobPayment.rejected, (state, action) => {
        state.loading.payment = false;
        state.error = action.payload?.message || "Failed to record payment";
      });

    // Add Job Deduction
    builder
      .addCase(addJobDeduction.pending, (state) => {
        state.loading.deduction = true;
        state.error = null;
      })
      .addCase(addJobDeduction.fulfilled, (state, action) => {
        state.loading.deduction = false;
        state.successMessage = "Deduction added";
        const data = action.payload?.data || action.payload;
        if (data?.deduction && state.currentJobPayments.pricing?.deductions) {
          state.currentJobPayments.pricing.deductions.push(data.deduction);
        }
        if (data?.pricing) {
          state.currentJobPayments.pricing = data.pricing;
          if (state.currentJob) {
            state.currentJob.pricing = data.pricing;
          }
        }
        if (data?.karigarPayable !== undefined) {
          if (state.currentJobPayments.karigarPayment) {
            state.currentJobPayments.karigarPayment.totalPayable =
              data.karigarPayable;
          }
        }
      })
      .addCase(addJobDeduction.rejected, (state, action) => {
        state.loading.deduction = false;
        state.error = action.payload?.message || "Failed to add deduction";
      });

    // ========================================================================
    // REMOVE JOB DEDUCTION (NEW)
    // ========================================================================
    builder
      .addCase(removeJobDeduction.pending, (state) => {
        state.loading.removeDeduction = true;
        state.error = null;
      })
      .addCase(removeJobDeduction.fulfilled, (state, action) => {
        state.loading.removeDeduction = false;
        state.successMessage = "Deduction removed";
        const { deductionIndex } = action.payload;
        const data = action.payload?.data || action.payload;

        // Remove from deductions array by index
        if (
          deductionIndex !== undefined &&
          state.currentJobPayments.pricing?.deductions
        ) {
          state.currentJobPayments.pricing.deductions.splice(deductionIndex, 1);
        }

        // Update pricing from response
        if (data?.pricing) {
          state.currentJobPayments.pricing = data.pricing;
          if (state.currentJob) {
            state.currentJob.pricing = data.pricing;
          }
        }

        // Update removed deduction info if provided
        if (data?.removedDeduction) {
          // Already removed above
        }
      })
      .addCase(removeJobDeduction.rejected, (state, action) => {
        state.loading.removeDeduction = false;
        state.error = action.payload?.message || "Failed to remove deduction";
      });

    // ========================================================================
    // PROGRESS
    // ========================================================================
    builder
      .addCase(addJobProgress.pending, (state) => {
        state.loading.progress = true;
        state.error = null;
      })
      .addCase(addJobProgress.fulfilled, (state, action) => {
        state.loading.progress = false;
        state.successMessage = "Progress updated";
      })
      .addCase(addJobProgress.rejected, (state, action) => {
        state.loading.progress = false;
        state.error = action.payload?.message || "Failed to update progress";
      });

    // ========================================================================
    // FETCH BY RELATIONS
    // ========================================================================

    // Fetch Jobs by Karigar
    builder
      .addCase(fetchJobsByKarigar.pending, (state) => {
        state.loading.list = true;
        state.error = null;
      })
      .addCase(fetchJobsByKarigar.fulfilled, (state, action) => {
        state.loading.list = false;
        const data = action.payload?.data || action.payload;
        state.jobs = data?.jobs || data || [];
      })
      .addCase(fetchJobsByKarigar.rejected, (state, action) => {
        state.loading.list = false;
        state.error = action.payload?.message || "Failed to fetch karigar jobs";
      });

    // Fetch Jobs by Order
    builder
      .addCase(fetchJobsByOrder.pending, (state) => {
        state.loading.list = true;
        state.error = null;
      })
      .addCase(fetchJobsByOrder.fulfilled, (state, action) => {
        state.loading.list = false;
        const data = action.payload?.data || action.payload;
        state.jobs = data?.jobs || data || [];
      })
      .addCase(fetchJobsByOrder.rejected, (state, action) => {
        state.loading.list = false;
        state.error = action.payload?.message || "Failed to fetch order jobs";
      });

    // Fetch Job Timeline
    builder
      .addCase(fetchJobTimeline.pending, (state) => {
        state.loading.timeline = true;
      })
      .addCase(fetchJobTimeline.fulfilled, (state, action) => {
        state.loading.timeline = false;
        state.jobTimeline = action.payload || [];
      })
      .addCase(fetchJobTimeline.rejected, (state, action) => {
        state.loading.timeline = false;
        state.error = action.payload?.message || "Failed to fetch timeline";
      });

    // ========================================================================
    // ORDER-KARIGAR INTEGRATION
    // ========================================================================

    // Fetch Available Karigars for Job
    builder
      .addCase(fetchAvailableKarigarsForJob.pending, (state) => {
        state.loading.availableKarigars = true;
        state.error = null;
      })
      .addCase(fetchAvailableKarigarsForJob.fulfilled, (state, action) => {
        state.loading.availableKarigars = false;
        const data = action.payload?.data || action.payload;
        state.availableKarigars = {
          productKarigars: data?.productKarigars || [],
          otherKarigars: data?.otherKarigars || [],
          allKarigars: data?.allKarigars || [],
          recommendations: data?.recommendations || null,
          summary: data?.summary || null,
        };
      })
      .addCase(fetchAvailableKarigarsForJob.rejected, (state, action) => {
        state.loading.availableKarigars = false;
        state.error =
          action.payload?.message || "Failed to fetch available karigars";
      });

    // Assign Karigar to Order Item
    builder
      .addCase(assignKarigarToOrderItem.pending, (state) => {
        state.loading.assign = true;
        state.error = null;
      })
      .addCase(assignKarigarToOrderItem.fulfilled, (state, action) => {
        state.loading.assign = false;
        state.successMessage = "Karigar assigned to order item";
      })
      .addCase(assignKarigarToOrderItem.rejected, (state, action) => {
        state.loading.assign = false;
        state.error =
          action.payload?.message || "Failed to assign karigar to order item";
      });

    // Reassign Karigar for Order Item
    builder
      .addCase(reassignKarigarForOrderItem.pending, (state) => {
        state.loading.reassign = true;
        state.error = null;
      })
      .addCase(reassignKarigarForOrderItem.fulfilled, (state, action) => {
        state.loading.reassign = false;
        state.successMessage = "Karigar reassigned for order item";
      })
      .addCase(reassignKarigarForOrderItem.rejected, (state, action) => {
        state.loading.reassign = false;
        state.error =
          action.payload?.message ||
          "Failed to reassign karigar for order item";
      });

    // Bulk Assign Karigars
    builder
      .addCase(bulkAssignKarigars.pending, (state) => {
        state.loading.bulkAssign = true;
        state.error = null;
      })
      .addCase(bulkAssignKarigars.fulfilled, (state, action) => {
        state.loading.bulkAssign = false;
        state.successMessage = "Karigars assigned in bulk";
      })
      .addCase(bulkAssignKarigars.rejected, (state, action) => {
        state.loading.bulkAssign = false;
        state.error =
          action.payload?.message || "Failed to bulk assign karigars";
      });

    // Get Order Karigar Status
    builder
      .addCase(getOrderKarigarStatus.pending, (state) => {
        state.loading.orderStatus = true;
        state.error = null;
      })
      .addCase(getOrderKarigarStatus.fulfilled, (state, action) => {
        state.loading.orderStatus = false;
        state.orderKarigarStatus = action.payload?.data || action.payload;
      })
      .addCase(getOrderKarigarStatus.rejected, (state, action) => {
        state.loading.orderStatus = false;
        state.error =
          action.payload?.message || "Failed to get order karigar status";
      });

    // Get Karigar Queue
    builder
      .addCase(getKarigarQueue.pending, (state) => {
        state.loading.karigarQueue = true;
        state.error = null;
      })
      .addCase(getKarigarQueue.fulfilled, (state, action) => {
        state.loading.karigarQueue = false;
        state.karigarQueue =
          action.payload?.data?.queue || action.payload || [];
      })
      .addCase(getKarigarQueue.rejected, (state, action) => {
        state.loading.karigarQueue = false;
        state.error = action.payload?.message || "Failed to get karigar queue";
      });

    // Get Order Karigar Work Status
    builder
      .addCase(getOrderKarigarWorkStatus.pending, (state) => {
        state.loading.orderStatus = true;
        state.error = null;
      })
      .addCase(getOrderKarigarWorkStatus.fulfilled, (state, action) => {
        state.loading.orderStatus = false;
        state.orderKarigarWorkStatus = action.payload?.data || action.payload;
      })
      .addCase(getOrderKarigarWorkStatus.rejected, (state, action) => {
        state.loading.orderStatus = false;
        state.error =
          action.payload?.message || "Failed to get order karigar work status";
      });

    // Assign Karigar via Order
    builder
      .addCase(assignKarigarViaOrder.pending, (state) => {
        state.loading.assign = true;
        state.error = null;
      })
      .addCase(assignKarigarViaOrder.fulfilled, (state, action) => {
        state.loading.assign = false;
        state.successMessage = "Karigar assigned via order";
      })
      .addCase(assignKarigarViaOrder.rejected, (state, action) => {
        state.loading.assign = false;
        state.error =
          action.payload?.message || "Failed to assign karigar via order";
      });

    // Check Order Can Fulfill
    builder
      .addCase(checkOrderCanFulfill.pending, (state) => {
        state.loading.canFulfill = true;
        state.error = null;
      })
      .addCase(checkOrderCanFulfill.fulfilled, (state, action) => {
        state.loading.canFulfill = false;
        state.canFulfillCheck = action.payload?.data || action.payload;
      })
      .addCase(checkOrderCanFulfill.rejected, (state, action) => {
        state.loading.canFulfill = false;
        state.error =
          action.payload?.message || "Failed to check order can fulfill";
      });

    // Send Job Message
    builder
      .addCase(sendJobMessage.pending, (state) => {
        state.loading.message = true;
        state.error = null;
      })
      .addCase(sendJobMessage.fulfilled, (state, action) => {
        state.loading.message = false;
        state.successMessage = "Message sent";
        const data = action.payload?.data || action.payload;
        if (data?.message && state.currentJob?.messages) {
          state.currentJob.messages.push(data.message);
        }
      })
      .addCase(sendJobMessage.rejected, (state, action) => {
        state.loading.message = false;
        state.error = action.payload?.message || "Failed to send message";
      });
  },
});

// ============================================================================
// EXPORTS
// ============================================================================

export const {
  clearError,
  clearSuccessMessage,
  setFilters,
  resetFilters,
  clearCurrentJob,
  clearAvailableKarigars,
  clearTimeline,
  clearOrderStatus,
  clearKarigarQueue,
  clearJobMaterials,
  clearJobPayments,
  clearJobImages,
  clearCanFulfillCheck,
  clearOrderKarigarWorkStatus,
  setMaterialPermissions,
  clearPricingPreview,
} = jobSlice.actions;

export default jobSlice.reducer;
